import React, { FocusEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { IGetSettingsResponse, IResponseError } from "../../../data-access";
import { HiOutlineBell } from "react-icons/hi2";
import { FormInput, FormButton } from "../../../ui";
import { notify, handleFailedRequest } from "../../../util";
import { developerApi } from "../../../api/developer-tools";
import "./webhook-url.scss";

const baseClass = "webhook-url";

export const WebhookForm = (): JSX.Element => {
  const [isProcessingForm, setIsProcessingForm] = useState<boolean>(false);

  const form = useForm<{ url: string }>({
    mode: "all",
    defaultValues: {
      url: "",
    },
  });
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields, isValid },
  } = form;

  useEffect(() => {
    developerApi.getWebhookURL().then((response: any) => {
      const {
        data: { url },
      } = response;
      setValue("url", url, {
        shouldDirty: true,
        shouldValidate: false,
        shouldTouch: true,
      });
    });
  }, []);

  const handleFormSubmit = (formFields: { url: string }): void => {
    setIsProcessingForm(true);
    developerApi
      .createWebhookUrl(formFields.url)
      .then((response: IGetSettingsResponse) => {
        notify("success", "Webhook URL has been successfully updated.");
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessingForm(false));
  };

  const isFormBeenModified = Object.keys(dirtyFields).length > 0;

  return (
    <form className={`${baseClass}`} onSubmit={handleSubmit(handleFormSubmit)}>
      <div className={`${baseClass}__note`}>
        <div className={`${baseClass}__note-icon`}>
          <HiOutlineBell size={24} />
        </div>
        <p className={`${baseClass}__note-text`}>
          Receive real-time notifications for job openings, bids, replies,
          direct messages, and invites. Make sure your server is ready to
          handle incoming requests.
        </p>
      </div>

      <div className={`${baseClass}__field`}>
        <label className={`${baseClass}__label`}>URL</label>
        <div className={`${baseClass}__input-row`}>
          <FormInput
            {...register("url", {
              required: "This field is required",
            })}
            value={watch("url")}
            disabled={isProcessingForm}
            error={errors?.url?.message}
            placeholder="Example: https://webhook.site"
            onChange={(e: FocusEvent<HTMLInputElement>) =>
              setValue("url", e?.target?.value, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              })
            }
          />
          <FormButton
            className={`${baseClass}__button`}
            theme="green-light"
            htmlType="submit"
            loading={isProcessingForm}
            disabled={!isFormBeenModified || !isValid || isProcessingForm}
          >
            Save
          </FormButton>
        </div>
      </div>
    </form>
  );
};

export default WebhookForm;
